import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Paper, Button, MenuItem, Select, FormControl, InputLabel, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress } from '@mui/material';
import { useMsal } from "@azure/msal-react";

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminDataExport() {
  const { instance, accounts } = useMsal();
  const [quarters, setQuarters] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/submissions/quarters`).then(r => setQuarters(r.data)).catch(() => setError('Failed to load quarters'));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setMsg('');
    setError('');
    try {
      const azureClientId = process.env.REACT_APP_AZURE_AD_CLIENT_ID || process.env.AZURE_AD_CLIENT_ID;
      const apiScope = `api://${azureClientId}/access_as_user`;
      const request = {
        scopes: [apiScope],
        account: accounts[0]
      };
      const response = await instance.acquireTokenSilent(request);
      const accessToken = response.accessToken;

      const res = await axios.get(`${API_URL}/api/submissions/export`, {
        params: { quarter: selectedQuarter },
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedQuarter.replace(' ', '_')}_submissions.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setMsg('Export successful! You may now delete this quarter\'s data.');
    } catch (e) {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };


  const handleDelete = async () => {
    setDeleting(true);
    setMsg('');
    setError('');
    try {
      await axios.delete(`${API_URL}/api/submissions/quarter`, { params: { quarter: selectedQuarter } });
      setMsg('Data deleted successfully.');
      setQuarters(qs => qs.filter(q => q !== selectedQuarter));
      setSelectedQuarter('');
    } catch (e) {
      setError('Delete failed.');
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        How to Keep Data Clean
      </Typography>
      <Box sx={{ mb: 3, maxWidth: 800 }}>
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          <li style={{ marginBottom: 12 }}>
            <Typography variant="body1">
              When ready to reset Usage data on the Field Specialist Scores workbook feeding Power BI, save the current Usage sheet in a separate workbook (for future reference), delete the current Usage sheet, and create a new Usage sheet (this new sheet will then populate with the next day's data)
            </Typography>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Typography variant="body1">
              To download a full quarter's worth of data, select the Quarter below and then click EXPORT AS CSV
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              If it is ever necessary to fully delete data from the backend database, you will be given that option after clicking EXPORT AS CSV - you do not need to delete old data unless storage becomes an issue
            </Typography>
          </li>
        </ol>
      </Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Quarterly Data Export & Cleanup</Typography>
      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Select Quarter</InputLabel>
          <Select
            value={selectedQuarter}
            label="Select Quarter"
            onChange={e => setSelectedQuarter(e.target.value)}
          >
            {quarters.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          sx={{ mr: 2 }}
          disabled={!selectedQuarter || exporting}
          onClick={handleExport}
        >
          {exporting ? <CircularProgress size={22} /> : 'Export as CSV'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={!selectedQuarter || deleting}
          onClick={() => setDeleteDialog(true)}
        >
          Delete Quarter
        </Button>
      </Paper>
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete all submissions for <b>{selectedQuarter}</b>? This cannot be undone. Make sure you have exported the data first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={22} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
